import { useEffect, useRef, useState } from "react";
import { ClipboardCheck, Check, RotateCcw, X, Send, Info, Loader2 } from "lucide-react";

/**
 * DemoApplicationModal — a fully self-contained, client-side simulated
 * reader application experience for regular (non-Early-Access) users on
 * demo/test projects.
 *
 * IMPORTANT: this component makes ZERO network calls. It touches no real
 * job, no application row, no author inbox, no messaging thread, no
 * analytics, no admin log, no payment record. Nothing here reaches the
 * real applications system.
 *
 * Field parity: cover letter (required), proposed rate (only when the
 * demo project is paid), and turnaround days — the same shape as the
 * real ``ApplyDialog`` in ``pages/JobDetail.jsx`` so the practice matches
 * the live flow.
 *
 * Phase machine:
 *   1. ``intro``    → user fills the form and taps "Submit demo application"
 *   2. ``pending``  → simulated 1.4s "submitting" spinner (client-side)
 *   3. ``accepted`` → simulated "Application received" confirmation with
 *                    a canned author auto-reply preview and a Reset /
 *                    Close footer
 */
const DEMO_NOTICE =
  "Demo / Practice Mode — No real author will receive anything, and no application, message, notification, or database record will be created.";

const CANNED_AUTHOR_AUTO_REPLY =
  "Thanks so much for applying! I'll review your note and get back to you shortly. In the real flow, this reply would land in your Coffee Chat / Messages inbox.";

const DEFAULT_COVER_LETTER =
  "Hi! I loved the sound of this project and I'd love to beta read for you.\n\nI usually flag pacing dips first, then character consistency, then prose. Happy to share a short sample of past notes if that'd help.";

export function DemoApplicationModal({ open, onClose, context = {} }) {
  const {
    targetName = "Demo Author",
    projectTitle = "Demo Project",
    unpaid = false,
  } = context;

  const [phase, setPhase] = useState("intro"); // intro | pending | accepted
  const [form, setForm] = useState({
    cover_letter: DEFAULT_COVER_LETTER,
    proposed_rate: "",
    proposed_turnaround_days: "",
  });
  const timers = useRef([]);

  useEffect(() => {
    if (!open) return;
    // Fully reset every time the modal is opened fresh.
    setPhase("intro");
    setForm({
      cover_letter: DEFAULT_COVER_LETTER,
      proposed_rate: "",
      proposed_turnaround_days: "",
    });
    return () => {
      // Clear any lingering setTimeouts on close/unmount so the phase
      // machine can't advance after the user has moved on.
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
  }, [open]);

  if (!open) return null;

  const coverLetter = form.cover_letter.trim();
  const canSubmit = coverLetter.length >= 10;

  const submit = () => {
    if (phase !== "intro" || !canSubmit) return;
    setPhase("pending");
    const t = setTimeout(() => setPhase("accepted"), 1400);
    timers.current.push(t);
  };

  const reset = () => {
    setPhase("intro");
    setForm({
      cover_letter: DEFAULT_COVER_LETTER,
      proposed_rate: "",
      proposed_turnaround_days: "",
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Demo application"
      data-testid="demo-application-modal"
    >
      <div className="w-full sm:max-w-lg bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl border border-border overflow-hidden flex flex-col max-h-[92vh]">
        <header className="px-5 pt-5 pb-3 border-b border-border/60 flex items-start gap-3 bg-[hsl(var(--sb-cream-2))]/60">
          <div className="mt-0.5 rounded-full bg-[hsl(var(--sb-clay))]/15 p-2 text-[hsl(var(--sb-clay))]">
            <ClipboardCheck size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--sb-clay))] font-semibold"
              data-testid="demo-application-eyebrow"
            >
              Demo Application · Practice mode
            </p>
            <h2 className="font-serif text-xl leading-tight">
              Apply to {projectTitle}
            </h2>
            <p
              className="mt-2 rounded-xl border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/15 px-3 py-2.5 text-sm text-foreground leading-relaxed flex items-start gap-1.5"
              data-testid="demo-application-notice"
              role="note"
            >
              <Info size={13} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
              <span><strong className="font-semibold">{DEMO_NOTICE}</strong></span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-muted transition"
            aria-label="Close demo application"
            data-testid="demo-application-close-btn"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 flex flex-col min-h-0">
          {phase === "intro" && (
            <div
              className="flex-1 overflow-y-auto p-5 space-y-4"
              data-testid="demo-application-intro"
              style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
            >
              <div>
                <label className="text-sm font-medium mb-1.5 block">Cover letter</label>
                <textarea
                  rows={6}
                  value={form.cover_letter}
                  onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))]/30"
                  placeholder="Why you'd love to read this book, what you bring…"
                  data-testid="demo-application-cover-letter"
                />
                {!canSubmit && coverLetter.length > 0 && (
                  <p className="mt-1 text-sm text-foreground/80">
                    Cover letter needs at least 10 characters.
                  </p>
                )}
              </div>
              <div className={unpaid ? "" : "grid grid-cols-2 gap-3"}>
                {!unpaid && (
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Proposed rate (optional)</label>
                    <input
                      type="number"
                      min={0}
                      value={form.proposed_rate}
                      onChange={(e) => setForm({ ...form, proposed_rate: e.target.value })}
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))]/30"
                      placeholder="150"
                      data-testid="demo-application-rate"
                    />
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Turnaround days (optional)</label>
                  <input
                    type="number"
                    min={1}
                    value={form.proposed_turnaround_days}
                    onChange={(e) => setForm({ ...form, proposed_turnaround_days: e.target.value })}
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))]/30"
                    placeholder="14"
                    data-testid="demo-application-turnaround"
                  />
                </div>
              </div>
              <p className="text-sm text-foreground/80">
                In a real application this would appear in the author's applications inbox. Here it stays entirely in your browser.
              </p>
            </div>
          )}

          {phase === "pending" && (
            <div
              className="flex-1 flex flex-col items-center justify-center p-10 text-center"
              data-testid="demo-application-pending"
            >
              <div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--sb-forest))]/30 border-t-[hsl(var(--sb-forest))] animate-spin" />
              <p className="mt-4 text-sm text-foreground/80">
                Submitting your practice application…
              </p>
            </div>
          )}

          {phase === "accepted" && (
            <div
              className="flex-1 overflow-y-auto p-6 text-center flex flex-col items-center"
              data-testid="demo-application-accepted"
            >
              <div className="h-11 w-11 rounded-full bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))] flex items-center justify-center">
                <Check size={22} />
              </div>
              <p className="mt-4 text-base font-semibold">Practice application received.</p>
              <p className="mt-1 text-sm text-foreground/80 max-w-sm">
                Nothing was saved or sent — this walkthrough only lives in your browser.
              </p>
              <div className="mt-5 w-full max-w-sm rounded-2xl border border-border bg-[hsl(var(--sb-cream-2))]/70 px-4 py-3 text-left">
                <p className="text-xs uppercase tracking-[0.14em] text-foreground/70 font-semibold">
                  Canned reply preview from {targetName}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground">{CANNED_AUTHOR_AUTO_REPLY}</p>
              </div>
            </div>
          )}
        </div>

        <footer className="border-t border-border/60 px-4 py-3 flex items-center gap-2 bg-[hsl(var(--sb-cream-2))]/40">
          {phase === "intro" ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-foreground/70 hover:text-foreground hover:underline"
                data-testid="demo-application-cancel-btn"
              >
                Cancel
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="rounded-full bg-[hsl(var(--sb-forest))] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="demo-application-submit-btn"
              >
                <Send size={14} /> Submit demo application
              </button>
            </>
          ) : phase === "pending" ? (
            <div className="w-full flex items-center justify-center text-sm text-foreground/80 gap-1.5">
              <Loader2 size={13} className="animate-spin" /> Practice submission in progress…
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={reset}
                className="text-sm font-medium text-foreground/70 hover:text-foreground hover:underline inline-flex items-center gap-1"
                data-testid="demo-application-reset-btn"
              >
                <RotateCcw size={13} /> Reset demo
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-border bg-background px-4 py-2 text-sm font-medium"
                data-testid="demo-application-end-btn"
              >
                End demo
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

export default DemoApplicationModal;
